import React, { useContext, useState } from 'react';
import { Types } from '../../../enums/TypeEnums';
import {
  ISiteSection,
  useCreateSiteSectionMutation,
  useUpdateSiteSectionMutation
} from '../../../generated/graphql';
import { sameSiteSectionNameExists } from '../../../utils/clientUtils';
import { AppContext } from '../../../AppContext';
import { Actions } from '../../../enums/ActionEnums';
import DialogBox from '../../Common/Layout/DialogBox';
import { Box } from '@mui/material';
import InputComponent from '../../Common/Fields/InputComponent';
import CancelButton from '../../Common/Buttons/CancelButton';
import SaveButton from '../../Common/Buttons/SaveButton';
import EditButton from '../../Common/Buttons/EditButton';
import { canUserEdit } from '../../../utils/rightUtils';
import { Colors } from '../../../utils/theme';

type IProps = {
  handleClose: (saved: boolean, isNew?: boolean, siteSectionInput?: ISiteSection) => void;
  siteSectionInput: ISiteSection | null;
};

export type IErrors = {
  name: string | null;
};

const defaultErrorsObj: IErrors = {
  name: null
};

const errorTexts = {
  name: 'Section name too short!',
  nameExists: 'Section name already exists'
};

const SiteSectionForm: React.FunctionComponent<IProps> = ({
  handleClose,
  siteSectionInput
}: IProps) => {
  const [editable, setEditable] = useState(siteSectionInput === null);
  const [name, setName] = useState(siteSectionInput?.name || '');
  const [formErrors, setFormErrors] = useState(defaultErrorsObj);
  const [createSection, createResult] = useCreateSiteSectionMutation();
  const { dispatch, state } = useContext(AppContext);
  const [updateSiteSection, updateSiteSectionResult] = useUpdateSiteSectionMutation();
  const canEdit = canUserEdit(state.user, Types.Index);

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value } = event.currentTarget;
    setFormErrors({ name: value && value.length > 0 ? null : errorTexts.name });
    setName(value);
  };

  const handleCancel = () => {
    if (editable && siteSectionInput !== null) {
      setName(siteSectionInput?.name);
      setFormErrors(defaultErrorsObj);
      setEditable(false);
    } else {
      handleClose(false);
    }
  };

  const handleSubmit = () => {
    const trimmedName = name ? name.trim() : '';
    const errors = {
      name: null
    } as IErrors;
    let hasErrors = false;
    if (trimmedName.length === 0) {
      hasErrors = true;
      errors.name = errorTexts.name;
    } else if (sameSiteSectionNameExists(trimmedName, siteSectionInput?.id)) {
      hasErrors = true;
      errors.name = errorTexts.nameExists;
    }
    if (hasErrors) {
      setFormErrors(errors);
    } else {
      handleSave(siteSectionInput === null ? 'create' : 'update');
    }
  };

  const handleSave = (type: 'create' | 'update') => {
    if (type === 'create') {
      createSection({
        variables: {
          name: (name as string).trim()
        },
        update(_, { data }) {
          handleClose(true, true, data?.result as ISiteSection);
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'success',
              text: `Site section ${name} created`
            }
          });
        }
      }).catch((error) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: error.message,
            autoHide: 6000
          }
        });
      });
    } else {
      updateSiteSection({
        variables: {
          id: siteSectionInput?.id as string,
          name: (name as string).trim()
        },
        update(_, { data }) {
          handleClose(true, false, data?.result as ISiteSection);
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'success',
              text: 'Site section updated'
            }
          });
        }
      }).catch((e) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: e.message
          }
        });
      });
    }
  };

  return (
    <DialogBox
      id="new-siteSection"
      width={500}
      title={siteSectionInput ? `${siteSectionInput.name} Details` : 'Create Site Section'}
      onClose={handleClose}
    >
      {siteSectionInput && (
        <Box sx={{ textAlign: 'right', width: '100%', marginTop: '10px', marginRight: '15px' }}>
          <EditButton
            id="site-section-form-edit-button"
            onClick={() => setEditable(true)}
            disabled={editable || !canEdit}
            editButtonColor={!canEdit || editable ? Colors.gray : Colors.skyBlue}
          />
        </Box>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(1, minmax(0, 1fr))',
          padding: '32px 30px',
          height: siteSectionInput ? '200px' : '240px'
        }}
      >
        <InputComponent
          id="siteSection-form-name"
          label="Section Name"
          value={name || ''}
          onChange={handleNameChange}
          errorMsg={formErrors.name}
          disabled={!editable || createResult.loading || updateSiteSectionResult.loading}
          additionalStyle={{ gridColumn: 'span 1' }}
        />
        <Box className="centralize" sx={{ gridColumn: 'span 1' }}>
          <CancelButton
            id="cancel-site-section-button"
            disabled={createResult.loading || updateSiteSectionResult.loading}
            onClick={handleCancel}
            text={siteSectionInput === null || editable ? 'Cancel' : 'Close'}
          />
          <SaveButton
            id="save-site-section-button"
            disabled={!editable || createResult.loading || updateSiteSectionResult.loading}
            onClick={handleSubmit}
            text={siteSectionInput === null ? 'Create' : 'Save'}
          />
        </Box>
      </Box>
    </DialogBox>
  );
};

export default SiteSectionForm;
